import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Link,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { login, clearError } from "../../store/slices/authSlice";
import Toast from "../../components/common/Toast";
import { validateEmail, validatePassword } from "../../utils/validation";
import QRCode from "react-qr-code";
import api, { buildLastLogoutReasonHeaders } from "../../services/api";
import { checkAuth } from "../../store/slices/authSlice";
import { applyWebSessionTokens } from "../../utils/sessionRevocation";

interface LoginFormValues {
  email: string;
  password: string;
}

interface LoginFormErrors {
  email: string;
  password: string;
}

type ToastSeverity = "success" | "error" | "info" | "warning";
function buildQrWebClientDevice() {
  if (typeof navigator === "undefined") {
    return {
      displayName: "Web browser",
      name: "Web browser",
      clientType: "web",
      browser: "Web browser",
      os: "",
      platform: "",
      userAgent: "",
    };
  }
  const ua = navigator.userAgent || "";
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  let plat = (nav.userAgentData?.platform || navigator.platform || "").trim();
  let browser = "Web browser";
  if (/Edg\//i.test(ua)) browser = "Microsoft Edge";
  else if (/OPR\/|Opera/i.test(ua)) browser = "Opera";
  else if (/Chrome\/|CriOS/i.test(ua)) browser = "Google Chrome";
  else if (/Firefox\//i.test(ua)) browser = "Mozilla Firefox";
  else if (/Safari/i.test(ua) && !/Chrome|Chromium|CriOS/i.test(ua)) browser = "Safari";
  if (!plat) {
    if (/Windows NT/i.test(ua)) plat = "Windows";
    else if (/Mac OS X|Macintosh/i.test(ua)) plat = "macOS";
    else if (/Linux/i.test(ua) && !/Android/i.test(ua)) plat = "Linux";
    else if (/Android/i.test(ua)) plat = "Android";
    else if (/iPhone|iPad|iPod/i.test(ua)) plat = "iOS";
  }
  const displayName = plat ? `${browser} (${plat})` : browser;
  return {
    displayName,
    name: displayName,
    clientType: "web",
    browser,
    os: plat,
    platform: plat,
    userAgent: ua,
  };
}
const QR_REFRESH_MS = 30000;
const QR_COUNTDOWN_TICK_MS = 1000;
const QR_STATUS_POLL_MS = 2000;

const Login: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, message, isAuthenticated } = useSelector(
    (state: { auth: Record<string, unknown> }) => state.auth
  );
  const [showPassword, setShowPassword] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<ToastSeverity>("success");

  const [formValues, setFormValues] = useState<LoginFormValues>({
    email: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState<LoginFormErrors>({
    email: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrErr, setQrErr] = useState<string | null>(null);
  const [qrSid, setQrSid] = useState<string | null>(null);
  const [qrNonce, setQrNonce] = useState<string | null>(null);
  const [qrRefreshDeadline, setQrRefreshDeadline] = useState<number | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrCountdown, setQrCountdown] = useState(0);
  const qrAutoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearQrAutoRefresh = useCallback(() => {
    if (qrAutoRefreshRef.current != null) {
      clearInterval(qrAutoRefreshRef.current);
      qrAutoRefreshRef.current = null;
    }
  }, []);
  const loadQr = useCallback(async () => {
    setQrErr(null);
    setQrLoading(true);
    try {
      const res = await api.post("/auth/qr/session");
      const d = res?.data?.data || {};
      setQrSid(d.payload?.sid || null);
      setQrNonce(d.payload?.nonce || null);
      const deadline = Date.now() + QR_REFRESH_MS;
      setQrRefreshDeadline(deadline);
      setQrCountdown(Math.max(0, Math.ceil((deadline - Date.now()) / QR_COUNTDOWN_TICK_MS)));
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setQrErr(err?.response?.data?.message || err?.message || "Failed to create QR session");
      setQrSid(null);
      setQrNonce(null);
      setQrRefreshDeadline(null);
      setQrCountdown(0);
    } finally {
      setQrLoading(false);
    }
  }, []);
  const startQrAutoRefresh = useCallback(() => {
    clearQrAutoRefresh();
    qrAutoRefreshRef.current = setInterval(() => {
      void loadQr();
    }, QR_REFRESH_MS);
  }, [clearQrAutoRefresh, loadQr]);
  useEffect(() => {
    if (!qrOpen) {
      clearQrAutoRefresh();
      setQrRefreshDeadline(null);
      setQrCountdown(0);
      return;
    }
    let cancelled = false;
    (async () => {
      await loadQr();
      if (!cancelled) startQrAutoRefresh();
    })();
    return () => {
      cancelled = true;
      clearQrAutoRefresh();
    };
  }, [qrOpen, loadQr, startQrAutoRefresh, clearQrAutoRefresh]);
  useEffect(() => {
    if (!qrOpen || !qrRefreshDeadline) return;
    const t = setInterval(() => {
      setQrCountdown(Math.max(0, Math.ceil((qrRefreshDeadline - Date.now()) / QR_COUNTDOWN_TICK_MS)));
    }, QR_COUNTDOWN_TICK_MS);
    return () => clearInterval(t);
  }, [qrOpen, qrRefreshDeadline]);

  useEffect(() => {
    if (!qrOpen || !qrSid || !qrNonce) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const st = await api.get(`/auth/qr/session/${qrSid}`);
        const status = st?.data?.data?.status;
        if (status === "approved") {
          const consume = await api.post(
            `/auth/qr/session/${qrSid}/consume`,
            {
              nonce: qrNonce,
              device: buildQrWebClientDevice(),
            },
            { headers: buildLastLogoutReasonHeaders() }
          );
          const token = consume?.data?.data?.token || consume?.data?.token;
          if (token) {
            // Stores both tokens (new backend issues a refreshToken for QR sessions).
            // clearMissingRefresh: never pair a QR access token with a stale refresh token.
            const qrRefresh = consume?.data?.data?.refreshToken;
            applyWebSessionTokens({
              accessToken: token,
              refreshToken: typeof qrRefresh === "string" && qrRefresh ? qrRefresh : null,
              clearMissingRefresh: true,
            });
          }
          await dispatch(checkAuth() as any);
          if (!cancelled) navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        const err = e as { message?: string };
        console.warn("[Login:qrTick] Error:", err?.message || e);
      }
    };
    const poll = setInterval(tick, QR_STATUS_POLL_MS);
    return () => { cancelled = true; clearInterval(poll); };
  }, [qrOpen, qrSid, qrNonce, dispatch, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const emailError = validateEmail(formValues.email);
    const passwordError = validatePassword(formValues.password);

    setFormErrors({
      email: emailError || "",
      password: passwordError || "",
    });

    if (emailError || passwordError) return;

    try {
      setSubmitting(true);
      try {
        // @ts-expect-error - login thunk from JS authSlice inferred as 0-arg
        const result = await dispatch(login(formValues));

        if (login.fulfilled.match(result as { type: string })) {
          const payload = (result as { payload?: Record<string, unknown> })
            ?.payload;

          const msg =
            (payload?.message as string) ||
            (payload?.data as Record<string, unknown>)?.message ||
            "Login successful!";

          if (payload?.isFirstLogin) {
            navigate("/welcome", {
              replace: true,
              state: { toastMessage: msg, toastSeverity: "success" },
            });
          } else if (payload?.consentRequired) {
            navigate("/consent", {
              replace: true,
              state: { toastMessage: msg, toastSeverity: "success" },
            });
          } else {
            navigate("/dashboard", {
              replace: true,
              state: { toastMessage: msg, toastSeverity: "success" },
            });
          }
        } else if (login.rejected.match(result as { type: string })) {
          const msg =
            (result as { payload?: string; error?: { message?: string } })
              ?.payload ||
            (result as { error?: { message?: string } })?.error?.message ||
            "Login failed. Please check your credentials.";
          setToastMessage(String(msg));
          setToastSeverity("error");
        }
      } catch (err) {
        const errObj = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const msg =
          errObj?.response?.data?.message ||
          errObj?.message ||
          "An error occurred during login";
        setToastMessage(msg);
        setToastSeverity("error");
      } finally {
        setSubmitting(false);
      }
    } catch {
      setSubmitting(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (message && isAuthenticated) {
      setToastMessage(String(message));
      setToastSeverity("success");
    }
  }, [message, isAuthenticated]);

  useEffect(() => {
    try {
      const logoutReason = localStorage.getItem("logoutReason");
      if (logoutReason) {
        setToastMessage(logoutReason);
        setToastSeverity("error");
        localStorage.removeItem("logoutReason");
      }
    } catch {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleCloseToast = () => {
    setToastMessage(null);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      <Typography variant="h5" textAlign="center" fontWeight={600} mb={3}>
        Sign In
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String(error)}
        </Alert>
      )}

      <TextField
        fullWidth
        id="email"
        name="email"
        label="Email Address *"
        placeholder="Enter your email"
        value={formValues.email}
        onChange={(e) => {
          const value = e.target.value;
          setFormValues((prev) => ({ ...prev, email: value }));
          const liveError = validateEmail(value);
          setFormErrors((prev) => ({ ...prev, email: liveError || "" }));
        }}
        onBlur={(e) => {
          const liveError = validateEmail(e.target.value);
          setFormErrors((prev) => ({ ...prev, email: liveError || "" }));
        }}
        error={!!formErrors.email}
        helperText={formErrors.email}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        id="password"
        name="password"
        label="Password *"
        type={showPassword ? "text" : "password"}
        placeholder="Enter your password"
        value={formValues.password}
        onChange={(e) => {
          const value = e.target.value;
          setFormValues((prev) => ({ ...prev, password: value }));
          const liveError = validatePassword(value);
          setFormErrors((prev) => ({ ...prev, password: liveError || "" }));
        }}
        onBlur={(e) => {
          const liveError = validatePassword(e.target.value);
          setFormErrors((prev) => ({ ...prev, password: liveError || "" }));
        }}
        error={!!formErrors.password}
        helperText={formErrors.password}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleTogglePassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Link
          component={RouterLink}
          to="/forgot-password"
          variant="body2"
          underline="hover"
        >
          Forgot Password?
        </Link>
        <Button variant="text" size="small" onClick={() => { setQrOpen(true); }}>
          Login with QR
        </Button>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={Boolean(loading) || submitting || !!formErrors.email || !!formErrors.password}
        sx={{
          py: 1.5,
          mb: 2,
          position: "relative",
          "&:disabled": {
            backgroundColor: "primary.main",
            opacity: 0.7,
          },
        }}
      >
        {Boolean(loading) || submitting ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress
              size={20}
              thickness={4}
              sx={{
                color: "white",
                animationDuration: "550ms",
              }}
            />
            <Typography variant="body2" sx={{ color: "white" }}>
              {Boolean(loading) ? "Checking session..." : "Signing in..."}
            </Typography>
          </Box>
        ) : (
          "Sign In"
        )}
      </Button>

      <Typography variant="body2" textAlign="center" color="text.secondary">
        Don&apos;t have an account? Contact on +91 9876213234
      </Typography>

      <Dialog open={qrOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setQrOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Login with QR</DialogTitle>
        <DialogContent>
          {qrErr ? <Alert severity="error" sx={{ mb: 2 }}>{qrErr}</Alert> : null}
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            {qrLoading ? <CircularProgress size={28} /> : (qrSid && qrNonce ? <QRCode value={JSON.stringify({ sid: qrSid, nonce: qrNonce })} size={220} /> : null)}
          </Box>
          {qrRefreshDeadline ? <Typography variant="caption" color="text.secondary" display="block" textAlign="center">Refreshes in {qrCountdown}s</Typography> : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              clearQrAutoRefresh();
              await loadQr();
              startQrAutoRefresh();
            }}
            disabled={qrLoading}
          >
            Refresh
          </Button>
          <Button onClick={() => { setQrOpen(false); }} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

export default Login;
